import { type CoreIcon } from '@pollux-docaposte/core-design-system'
import { Component, Host, h, Prop, Event, type EventEmitter } from '@stencil/core'

@Component({
  tag: 'cobalt-icon-button',
  styleUrl: 'cobalt-icon-button.css'
})
export class CobaltIconButton {
  /**
   * This is the text which describe the button. It is required for **accessibility** concerns. It will appear in a tooltip.
   */
  @Prop() label!: string
  /**
   * A string indicating the behavior of the button. It relies on `HTMLButtonElement['type']`
   */
  @Prop() type?: HTMLButtonElement['type'] = 'button'
  /**
   * A string indicating the design variation of the button based on the level of importance.
   */
  @Prop() variant?: 'primary' | 'secondary' | 'tertiary' | 'quaternary' = 'primary'
  /**
   * A boolean indicating the disable state of the button. The `aria-disabled` attribute relies on this property.
   */
  @Prop({ mutable: true }) disabled?: boolean = false
  /**
   * A string indicating the size variation of the button.
   */
  @Prop({ mutable: true }) size?: 'small' | 'medium' | 'large' = 'medium'
  /**
   * Accept all icon names from @pollux-docaposte/icon-library. By using the core-icon-button component you don't have to install it as dependency.
   *
   * The icon render only if `iconName` is defined.
   */
  @Prop({ mutable: true }) iconName?: CoreIcon['name'] = undefined
  /**
   * Tooltip alignment prop to where the tooltip will appear
   */
  @Prop({ reflect: true }) tooltipDirection?: 'top' | 'right' | 'bottom' | 'left' = 'top'

  /**
   * Click event on the button
   */
  @Event() cobaltIconButtonClick: EventEmitter

  /**
   * Handle the click event on the button.
   */
  private readonly handleClick = (ev: Event): void => {
    if (this.disabled) {
      ev.preventDefault()
      ev.stopPropagation()
      return
    }
    this.cobaltIconButtonClick.emit()
  }

  render(): any {
    return (
      <Host>
        <core-icon-button
          type={this.type}
          label={this.label}
          variant={this.variant}
          size={this.size}
          disabled={this.disabled}
          icon-name={this.iconName}
          tooltip-direction={this.tooltipDirection}
          onCoreIconButtonClick={this.handleClick}
        ></core-icon-button>
      </Host>
    )
  }
}
